/**
 * An OPTIONAL read, bounded (#580 sweep, fix round 1).
 *
 * A screen that loads its own data with `Promise.all` waits for the slowest member, so an extra
 * read that never settles decides when the screen appears. `.catch(() => null)` covers only the
 * rejection path. This covers the never-settles path too: past `ms`, the read counts as not read.
 *
 * The request is NOT aborted. The caller's `signal` belongs to the whole load, and aborting it
 * would take the reads that did answer down with it.
 */
export const SOFT_READ_TIMEOUT_MS = 4000;
// Tenant preferences enhance these screens; a slow preference read must not hold their data. This is
// how long past the screen's REQUIRED reads it may wait (see `softReadAfter`).
export const TENANT_READ_TIMEOUT_MS = 500;

/** `promise`'s value, or `null` when it rejects or has not settled within `ms`. Never rejects. */
export async function softRead(promise, ms = SOFT_READ_TIMEOUT_MS) {
  let timer;
  const deadline = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), ms);
  });
  try {
    return await Promise.race([Promise.resolve(promise).catch(() => null), deadline]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * An OPTIONAL read beside a screen's REQUIRED reads: `promise`'s value, or `null` when it rejects or has
 * not settled `graceMs` after `gate` (the required reads) settles. Never rejects.
 *
 * The deadline starts when the screen could appear, not when the read started. A slow network slows
 * every read, and an answer that arrives while the screen is still waiting for its required reads
 * costs the screen nothing, so it is kept. The screen still never waits more than `graceMs` for it.
 *
 * Pass the screen's LAST required round as `gate`. A first round that a second round follows is not
 * when the screen could appear (Qwen q3 M1). The cost, accepted: once every required read is in, a
 * read that never answers holds the screen `graceMs` longer (Qwen q3 L1).
 */
export async function softReadAfter(promise, gate, graceMs = TENANT_READ_TIMEOUT_MS) {
  let timer;
  let settled = false;
  const deadline = Promise.resolve(gate).catch(() => undefined).then(() => new Promise((resolve) => {
    if (settled) { resolve(null); return; }
    timer = setTimeout(() => resolve(null), graceMs);
  }));
  try {
    return await Promise.race([Promise.resolve(promise).catch(() => null), deadline]);
  } finally {
    settled = true;
    clearTimeout(timer);
  }
}
